import type { PlayerStatsBatch, PlayerStatsSample } from '@core/types';

export const PLAYER_STATS_QUEUE_LIMIT = 6;

/**
 * Holds player samples until the hub is connected. One invoke is in flight at a time; a sample is
 * removed only after the server accepted the batch it was in, so a failed invoke simply retries on
 * the next flush (the next sample, or a reconnect). A 4xx answer (e.g. AccessDenied) will never
 * succeed, so that batch is dropped instead of retried.
 */
export class PlayerStatsQueue {
    private samples: PlayerStatsSample[] = [];
    private inFlight = false;

    constructor(
        private readonly send: (batch: PlayerStatsBatch) => Promise<{ success: boolean; statusCode?: number }>,
        private readonly isConnected: () => boolean,
        private readonly now: () => number = Date.now
    ) {}

    get size(): number {
        return this.samples.length;
    }

    /** Drops all queued samples, e.g. when switching to another webinar's connection. */
    clear(): void {
        this.samples = [];
    }

    push(sample: PlayerStatsSample): void {
        this.samples.push(sample);
        if (this.samples.length > PLAYER_STATS_QUEUE_LIMIT) {
            this.samples.splice(0, this.samples.length - PLAYER_STATS_QUEUE_LIMIT);
        }
        void this.flush();
    }

    async flush(): Promise<void> {
        if (this.inFlight || this.samples.length === 0 || !this.isConnected()) {
            return;
        }

        const batch = this.samples.slice();
        this.inFlight = true;
        let accepted = false;
        try {
            const response = await this.send({ sentAt: this.now(), samples: batch });
            const rejected =
                !response.success &&
                response.statusCode !== undefined &&
                response.statusCode >= 400 &&
                response.statusCode < 500;
            accepted = response.success || rejected;
            if (accepted) {
                this.samples = this.samples.filter((s) => !batch.includes(s));
            }
        } catch {
            // Keep the samples for the next flush
        } finally {
            this.inFlight = false;
        }

        if (accepted && this.samples.length > 0) {
            void this.flush();
        }
    }
}
